import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router';
import { FiChevronDown, FiGlobe, FiMenu, FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import logoImage from '../../assets/images/brand/coolandcarelogo.png';

const MenuIcon = FiMenu as React.ComponentType<{ className?: string }>;
const CloseIcon = FiX as React.ComponentType<{ className?: string }>;
const GlobeIcon = FiGlobe as React.ComponentType<{ className?: string }>;
const ChevronDownIcon = FiChevronDown as React.ComponentType<{ className?: string }>;

const navItems = [
  { to: '/', labels: { en: 'Home', fr: 'Accueil', zh: '首页' } },
  { to: '/solutions', labels: { en: 'Solutions', fr: 'Solutions', zh: '解决方案' } },
  { to: '/partners', labels: { en: 'Partners', fr: 'Partenaires', zh: '合作伙伴' } },
  { to: '/industries', labels: { en: 'Industries', fr: 'Industries', zh: '行业' } },
  { to: '/projects', labels: { en: 'Projects', fr: 'Projets', zh: '项目' } },
  { to: '/about', labels: { en: 'About', fr: 'À Propos', zh: '关于我们' } },
  { to: '/contact', labels: { en: 'Contact', fr: 'Contact', zh: '联系我们' } },
];

const languageOptions = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'French' },
  { code: 'zh', label: 'Chinese' },
];

const Navbar: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();


  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setIsLanguageOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = isOpen ? 'hidden' : '';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0] as 'en' | 'fr' | 'zh';
  const currentLanguageLabel =
    t(`header.languages.${currentLanguage}`, { defaultValue: 'English' });
  const languageTitleByCode: Record<'en' | 'fr' | 'zh', string> = {
    en: 'Language',
    fr: 'Langue',
    zh: '语言',
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    setIsLanguageOpen(false);
  };


  const headerClassName = isScrolled
    ? 'border-slate-200 bg-white shadow-[0_6px_24px_rgba(15,23,42,0.08)]'
    : 'border-slate-200 bg-white';

  const mobilePanelClassName = 'border-slate-200 bg-white';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerClassName}`}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-[86px]">
        <Link to="/" className="flex items-center gap-3 group" onClick={() => setIsOpen(false)}>
          <img src={logoImage} alt="Cool & Care" className="h-12 w-auto transition-transform group-hover:scale-105 lg:h-14" />
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="text-[15px] font-medium text-slate-600 hover:text-teal-700 transition-colors px-3.5 py-2.5 hover:bg-teal-50"
            >
              {item.labels[currentLanguage] || item.labels.en}
            </Link>
          ))}
          <div className="relative ml-2">
            <button
              type="button"
              onClick={() => setIsLanguageOpen((prev) => !prev)}
              className="inline-flex items-center gap-1.5 text-[15px] font-medium text-slate-600 hover:text-teal-700 transition-colors px-3 py-2.5 hover:bg-teal-50"
            >
              <GlobeIcon className="h-[18px] w-[18px]" />
              <span>{currentLanguageLabel}</span>
              <ChevronDownIcon className={`h-4 w-4 transition-transform ${isLanguageOpen ? 'rotate-180' : ''}`} />
            </button>
            {isLanguageOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 w-36 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
                {languageOptions.map((item) => (
                  <button
                    key={item.code}
                    type="button"
                    onClick={() => handleLanguageChange(item.code)}
                    className={`block w-full px-3 py-2 text-left text-sm transition ${
                      currentLanguage === item.code
                        ? 'bg-teal-50 text-teal-700 font-semibold'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Link
            to="/contact"
            className="ml-4 bg-teal-500 hover:bg-teal-400 text-white text-[15px] font-semibold px-6 py-3 transition-all hover:shadow-lg hover:shadow-teal-500/20"
          >
            {t('common.getQuote', { defaultValue: 'Get a Quote' })}
          </Link>
        </nav>


        <button
          type="button"
          aria-label="Toggle menu"
          className="inline-flex h-11 w-11 items-center justify-center text-slate-700 xl:hidden"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          {isOpen ? <CloseIcon className="text-[30px]" /> : <MenuIcon className="text-[30px]" />}
        </button>
      </div>

      <div
        className={`absolute left-0 right-0 top-[86px] z-50 border-t xl:hidden origin-top overflow-hidden transform-gpu transition-all duration-700 ease-out ${
          isOpen ? 'max-h-[85vh] scale-y-100 opacity-100' : 'max-h-0 scale-y-95 opacity-0 pointer-events-none'
        } ${mobilePanelClassName}`}
      >

          <nav className="flex flex-col px-6 pb-6 pt-8">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="text-slate-600 hover:text-teal-700 text-sm font-medium transition-colors px-4 py-3 rounded-lg hover:bg-teal-50"
                  onClick={() => setIsOpen(false)}
                >
                  {item.labels[currentLanguage] || item.labels.en}
                </Link>
              ))}
              <div className="mt-2 rounded-lg border border-slate-200 p-3">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                  {languageTitleByCode[currentLanguage]}
                </p>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {languageOptions.map((item) => (
                    <button
                      key={item.code}
                      type="button"
                      onClick={() => handleLanguageChange(item.code)}
                      className={`rounded-md px-2 py-2 text-xs font-semibold transition ${
                        currentLanguage === item.code
                          ? 'bg-teal-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {item.code.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <Link
                to="/contact"
                className="bg-teal-500 text-white text-sm font-semibold px-5 py-3 text-center mt-3"
                onClick={() => setIsOpen(false)}
              >
                {t('common.getQuote', { defaultValue: 'Get a Quote' })}
              </Link>
          </nav>
      </div>
    </header>
  );
};

export default Navbar;
